import { useCallback, useMemo } from 'react';

import { Recipient } from '@bonusly/recipient-entities';

import { RichMediaState } from '../../../components/action-input/lib/state/rich-media-state';
import { useFetchRecipients } from '../../../components/recognition/give-box/hooks/use-fetch-recipients';
import { NominatedGroup, NomineeGroupType } from '../../../schemas/nominations';

const recipientToNominatedGroup = (recipient: Recipient): NominatedGroup | null => {
	if (recipient.type !== 'group') return null;
	if (recipient.dimension !== 'department' && recipient.dimension !== 'location' && recipient.dimension !== 'team') {
		return null;
	}

	return {
		groupType: recipient.dimension as NomineeGroupType,
		key: recipient.id,
		displayName: recipient.displayName,
		userIds: recipient.userIds ?? [],
	};
};

// Adapter that turns the give-box `useFetchRecipients` response into a flat list
// of NominatedGroup records constrained to a specific group dimension. We reuse
// the same /v2/users/autocomplete?search_usergroups=true plumbing the give-box
// uses so there's a single source of truth for group resolution.
export const useSearchNomineeGroups = (groupType: NomineeGroupType) => {
	const fetchRecipients = useFetchRecipients({ search_usergroups: true, search_users: false, include_self: true });
	// Memoized empty state object so the underlying useFetchRecipients caller doesn't see
	// a fresh reference on every search and re-key its query cache entries.
	const emptyState = useMemo(() => new RichMediaState(), []);

	return useCallback(
		async (query: string): Promise<NominatedGroup[]> => {
			const recipients = await fetchRecipients({ query, state: emptyState });
			return recipients.map(recipientToNominatedGroup).filter((group): group is NominatedGroup => group !== null && group.groupType === groupType);
		},
		[fetchRecipients, groupType, emptyState]
	);
};
